import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Input, message, Popconfirm, Space, Spin, Table } from "antd";
import { productApi } from "api/product.api";
import { Pagination } from "components/Pagination";
import { useEffect, useState } from "react";
import { ModalStatus } from "types/modal";
import { Product } from "types/product";
import { QueryParam } from "types/query";
import { formatVND } from "utils";
import { formatDate } from "utils/date";
import { $url } from "utils/url";
import { ProductModal } from "../ProductModal";
import { ProductRateModal } from "../ProductRateModal";

const { ColumnGroup, Column } = Table;

export const ProductTab = ({ shopId }: { shopId: number }) => {
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 50,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [visibleModal, setVisibleModal] = useState(false);
  const [visibleRateModal, setVisibleRateModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Partial<Product>>({});
  const [modalStatus, setModalStatus] = useState<ModalStatus>("create");

  useEffect(() => {}, []);

  useEffect(() => {
    if (shopId) {
      fetchData();
    }
  }, [query]);

  useEffect(() => {
    if (shopId) {
      query.page = 1;
      query.search = "";
      setQuery({
        ...query,
      });
    }
  }, [shopId]);

  const fetchData = async () => {
    setLoading(true);
    const res = await productApi.findAll({
      ...query,
      shopId,
    });
    setLoading(false);
    setData(res.data.products);
    setTotal(res.data.total);
  };

  const handleDelete = async (row: Product) => {
    setLoading(true);
    try {
      await productApi.delete(row.id);
      fetchData();
      message.success("Xóa thành công");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="filter-container">
        <Space>
          <div className="filter-item">
            <label htmlFor="">Tìm kiếm</label>
            <Input
              onKeyDown={(ev) => {
                if (ev.code == "Enter") {
                  setQuery({ ...query });
                }
              }}
              size="middle"
              onChange={(ev) => {
                query.search = ev.currentTarget.value;
              }}
              placeholder="Tìm kiếm"
            />
          </div>

          <div className="filter-item btn">
            <Button
              onClick={fetchData}
              type="primary"
              icon={<SearchOutlined />}
            >
              Tìm kiếm
            </Button>
          </div>

          <div className="filter-item btn">
            <Button
              onClick={() => {
                setVisibleModal(true);
                setModalStatus("create");
                setSelectedProduct({});
              }}
              type="primary"
              icon={<PlusOutlined />}
            >
              Tạo mới sản phẩm
            </Button>
          </div>
        </Space>
      </div>

      <Spin spinning={loading}>
        <Table pagination={false} rowKey="id" dataSource={data}>
          <Column
            title="Tên sản phẩm"
            dataIndex="name"
            key="name"
            render={(text, record: Product) => {
              return (
                <div>
                  {record.image && (
                    <img
                      src={$url(record.image)}
                      width={40}
                      height={40}
                      style={{ objectFit: "cover", borderRadius: 4 }}
                      alt=""
                    />
                  )}
                  <br />
                  <span>{text}</span>
                </div>
              );
            }}
          />

          <Column
            title="Danh mục"
            dataIndex=""
            render={(text, record: Product) => (
              <span>{record.productCategory?.name}</span>
            )}
            key="originPrice"
          />

          <Column
            title="Đã bán"
            render={(text, record: Product) => (
              <span>{formatVND(record.sold)}</span>
            )}
          />

          <Column
            title="Tình trạng kho"
            render={(text, record: Product) => (
              <span>{record.isOutOfStock ? "Hết hàng" : "Còn hàng"}</span>
            )}
          />

          <Column
            title="Giá"
            align="right"
            dataIndex="originPrice"
            render={(text, record: Product) => {
              if (record.finalPrice) {
                return (
                  <>
                    <del style={{ fontSize: "smaller", color: "#999" }}>
                      {formatVND(record.originPrice)}
                    </del>
                    <br />
                    <span>{formatVND(record.finalPrice)}</span>
                  </>
                );
              } else {
                return <span>{formatVND(record.originPrice)}</span>;
              }
            }}
            key="originPrice"
          />

          <Column
            title="Thời gian đăng"
            render={(text, record: Product) => (
              <span>{formatDate(record.createdAt)}</span>
            )}
          />

          <Column
            title="Thao tác"
            key="action"
            render={(text, record: Product) => (
              <span>
                <Space>
                  <Button
                    type="primary"
                    onClick={() => {
                      setSelectedProduct(record);
                      setVisibleModal(true);
                      setModalStatus("update");
                    }}
                  >
                    Cập nhật
                  </Button>

                  <Popconfirm
                    placement="top"
                    title="Xác nhận xóa sản phẩm này"
                    cancelText="Đóng"
                    okText="Xóa"
                    onConfirm={() => handleDelete(record)}
                  >
                    <Button danger>Xóa</Button>
                  </Popconfirm>

                  <Button
                    type="primary"
                    onClick={() => {
                      setSelectedProduct(record);
                      setVisibleRateModal(true);
                    }}
                  >
                    Đánh giá
                  </Button>
                </Space>
              </span>
            )}
          />
        </Table>

        <Pagination
          currentPage={query.page}
          total={total}
          onChange={({ limit, page }) => {
            query.page = page;
            query.limit = limit;
            setQuery({ ...query });
          }}
        />
      </Spin>

      <ProductModal
        shopId={shopId}
        onSubmitOk={fetchData}
        onClose={() => setVisibleModal(false)}
        visible={visibleModal}
        product={selectedProduct}
        status={modalStatus}
      />

      <ProductRateModal
        visible={visibleRateModal}
        status={"create"}
        product={selectedProduct}
        onClose={() => setVisibleRateModal(false)}
      />
    </div>
  );
};
